import type { Variants } from 'motion/react';
import type React from 'react';
import { useScrollLock } from '@base-ui/utils/useScrollLock';
import { useQuery } from '@tanstack/react-query';
import { linkOptions } from '@tanstack/react-router';
import { Duration } from 'luxon';
import { motion } from 'motion/react';
import { useMemo } from 'react';
import { PiGlobe } from 'react-icons/pi';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDialog } from '@_/hooks';
import { Locales } from '@_/static';
import { Link } from '@_/ui';
import { isTruthy } from '@_/utils';
import { getReactQueryOptions } from '@_/utils-frontend';
import { publicApiQueryClient } from '#src/api.ts';
import { SelectLocaleDialog } from '#src/components/dialogs/SelectLocaleDialog.tsx';
import { buttonI18n, navI18n } from '#src/i18nMessages/index.ts';
import { useStores } from '#src/state/StoreContext.tsx';

const menuVariants: Variants = {
  closed: {
    x: '-100%',
    transition: { type: 'tween', ease: 'circOut' }
  },
  opened: {
    x: 0,
    transition: { type: 'tween', ease: 'anticipate' }
  }
};

const containerVariants: Variants = {
  opened: {
    transition: { delayChildren: 0.06, staggerChildren: 0.018 }
  }
};

const itemVariants: Variants = {
  closed: {
    opacity: 0,
    x: -24,
    transition: { type: 'tween' }
  },
  opened: {
    opacity: 1,
    x: 0,
    transition: { type: 'tween' }
  }
};

export function MobileNav({ onClose, open }: { onClose: () => void; open: boolean }) {
  const { locale } = useIntl();
  const { useAuthStore } = useStores();
  const logout = useAuthStore(state => state.logout);
  const user = useAuthStore(state => state.user);

  useScrollLock(open);

  const localeDialog = useDialog({
    component: SelectLocaleDialog
  });

  const categoryNavQuery = useQuery({
    ...getReactQueryOptions(publicApiQueryClient.metadata.getCategoryNav),
    staleTime: Duration.fromObject({ minutes: 5 }).toMillis()
  });

  const categoryNavItems = useMemo(
    () => categoryNavQuery.data?.data || [],
    [categoryNavQuery.data]
  );

  const navItems = [
    {
      id: 'home',
      label: <FormattedMessage id="nav.home" defaultMessage="Home" />,
      link: linkOptions({
        from: '/{-$locale}',
        to: '/{-$locale}'
      })
    },
    ...categoryNavItems.map(item => ({
      id: item.categoryId,
      label: item.label,
      link: linkOptions({
        from: '/{-$locale}',
        to: '/{-$locale}/courses/$categorySlug',
        params: prev => ({
          locale: prev.locale,
          categorySlug: item.slug
        })
      })
    })),
    {
      id: 'schools',
      label: <FormattedMessage {...navI18n.browseSchools} />,
      link: linkOptions({
        from: '/{-$locale}',
        to: '/{-$locale}/schools'
      })
    },
    {
      id: 'separator'
    },
    !user && {
      id: 'login',
      label: <FormattedMessage {...buttonI18n.logIn} />,
      link: linkOptions({
        from: '/{-$locale}',
        to: '/{-$locale}/login'
      })
    },
    !!user && {
      id: 'bookings',
      label: <FormattedMessage {...navI18n.bookings} />,
      link: linkOptions({
        from: '/{-$locale}',
        to: '/{-$locale}/bookings'
      })
    },
    !!user && {
      id: 'saved',
      label: <FormattedMessage {...navI18n.saved} />,
      link: linkOptions({
        from: '/{-$locale}',
        to: '/{-$locale}/saved'
      })
    },
    !!user && {
      id: 'settings',
      label: <FormattedMessage {...navI18n.accountSettings} />,
      link: linkOptions({
        from: '/{-$locale}',
        to: '/{-$locale}/settings'
      })
    },
    !!user && {
      id: 'logout',
      label: <FormattedMessage {...buttonI18n.logOut} />,
      link: linkOptions({
        from: '/{-$locale}',
        to: '/{-$locale}'
      }),
      onClick: (event: React.MouseEvent) => {
        event.preventDefault();
        void logout();
      }
    }
  ].filter(isTruthy);

  return (
    <>
      {localeDialog.element}

      <motion.div
        animate={open ? 'opened' : 'closed'}
        className="fixed inset-0 z-40 overflow-auto bg-gray-900 px-6"
        initial="closed"
        variants={menuVariants}
      >
        <motion.div
          animate={open ? 'opened' : 'closed'}
          className="flex min-h-screen flex-col justify-between pt-20 pb-8"
          initial="closed"
          variants={containerVariants}
        >
          <ul className="flex flex-col gap-y-4">
            {navItems.map(item => (
              <motion.li key={item.id} variants={itemVariants}>
                {item.id === 'separator' ? (
                  <div className="my-8 h-0.5 w-5 bg-white" />
                ) : (
                  <Link
                    {...item.link}
                    className="text-xl text-white"
                    onClick={event => {
                      if ('onClick' in item && item.onClick) {
                        item.onClick(event);
                      }
                      onClose();
                    }}
                    plain
                  >
                    {item.label}
                  </Link>
                )}
              </motion.li>
            ))}
          </ul>

          <motion.div variants={itemVariants}>
            <button
              className="mt-20 flex items-center text-gray-300 focus:outline-hidden"
              onClick={() => localeDialog.open()}
              type="button"
            >
              <PiGlobe className="size-6" />
              <div className="ml-2 text-sm">{Locales.find(loc => loc.code === locale)?.name}</div>
            </button>
          </motion.div>
        </motion.div>
      </motion.div>
    </>
  );
}
